import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Heart,
  Users,
  Wallet,
  Home,
  FileText,
  HandshakeIcon,
  MessageCircle,
  CheckCircle2,
  ChevronDown,
  HelpCircle,
} from "lucide-react";

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_direito-trabalho-sp/artifacts/8b5v8061_IconOnly_Transparent_NoBuffer.png";
const WHATSAPP_LINK = "https://wa.me/5511917611575?text=Olá! Gostaria de uma consulta sobre Direito de Família.";

const FamiliaPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      pergunta: "Quanto tempo demora um processo de divórcio?",
      resposta: "Depende do tipo. O divórcio consensual (acordo entre as partes) pode ser feito em cartório em poucos dias, se não houver filhos menores. O divórcio litigioso (sem acordo) pode levar de 6 meses a 2 anos, dependendo das questões a serem decididas (guarda, pensão, partilha)."
    },
    {
      pergunta: "Como é calculada a pensão alimentícia?",
      resposta: "Não existe uma fórmula fixa na lei. O juiz analisa as necessidades de quem recebe (alimentando) e as possibilidades de quem paga (alimentante). Em geral, varia entre 15% a 30% dos rendimentos líquidos, mas cada caso é analisado individualmente."
    },
    {
      pergunta: "O que é guarda compartilhada?",
      resposta: "É a modalidade em que ambos os pais dividem as responsabilidades sobre os filhos, participando ativamente das decisões importantes (educação, saúde, lazer). A criança pode ter residência fixa com um dos pais, mas ambos têm poder de decisão igualitário."
    },
    {
      pergunta: "Posso pedir revisão de pensão alimentícia?",
      resposta: "Sim, sempre que houver mudança na situação financeira de quem paga ou nas necessidades de quem recebe. Exemplos: perda de emprego, nascimento de outro filho, aumento das despesas com educação. A revisão deve ser feita judicialmente."
    },
    {
      pergunta: "Como funciona a partilha de bens no divórcio?",
      resposta: "Depende do regime de bens do casamento. Na comunhão parcial (mais comum), dividem-se os bens adquiridos durante o casamento. Na comunhão universal, todos os bens são divididos. Na separação total, cada um fica com o que é seu. Bens recebidos por herança ou doação geralmente não entram na partilha."
    },
    {
      pergunta: "O que acontece se o pai não pagar a pensão?",
      resposta: "O devedor pode ser executado judicialmente. A lei permite prisão civil de 1 a 3 meses pelo não pagamento de pensão. Também é possível a penhora de bens, bloqueio de contas bancárias, desconto em folha de pagamento e protesto do nome."
    },
  ];

  const servicos = [
    { icon: <FileText className="w-5 h-5" />, title: "Divórcio", desc: "Consensual ou litigioso, judicial ou extrajudicial" },
    { icon: <Wallet className="w-5 h-5" />, title: "Pensão Alimentícia", desc: "Fixação, revisão e execução de alimentos" },
    { icon: <Users className="w-5 h-5" />, title: "Guarda de Filhos", desc: "Unilateral, compartilhada e modificação" },
    { icon: <Heart className="w-5 h-5" />, title: "Regulamentação de Visitas", desc: "Regime de convivência entre pais e filhos" },
    { icon: <Home className="w-5 h-5" />, title: "Partilha de Bens", desc: "Divisão do patrimônio comum do casal" },
    { icon: <HandshakeIcon className="w-5 h-5" />, title: "União Estável", desc: "Reconhecimento, dissolução e efeitos" },
  ];

  return (
    <>
      <Helmet>
        <title>Advogado de Família | Divórcio e Guarda</title>
        <meta name="description" content="Advogado de família especializado. Atuação em divórcio, pensão alimentícia, guarda, regulamentação de visitas e partilha de bens." />
        <meta name="keywords" content="advogado de família, divórcio, pensão alimentícia, guarda de filhos, direito de família" />
      </Helmet>

      <div>
        {/* Hero */}
        <section className="pt-32 pb-20 bg-gradient-to-br from-rose-50 to-white relative overflow-hidden">
          <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.03] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[500px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <p className="text-rose-700 font-medium tracking-wider text-sm mb-4">DIREITO DE FAMÍLIA</p>
            <h1
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 max-w-3xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Advogado de <span className="text-rose-700">Família</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mb-8">
              Atuação em divórcio, pensão alimentícia, guarda, regulamentação de visitas 
              e partilha de bens, com abordagem técnica e sensível.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-rose-700 hover:bg-rose-800 text-white px-8 py-4 rounded font-medium transition-all">
                <MessageCircle className="w-5 h-5" />
                Fale com Especialista
              </a>
              <Link to="/contato"
                className="inline-flex items-center justify-center border-2 border-gray-300 hover:border-rose-700 text-gray-700 hover:text-rose-700 px-8 py-4 rounded font-medium transition-all">
                Enviar Mensagem
              </Link>
            </div>
          </div>
        </section>

        {/* Quando procurar */}
        <section className="py-16 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Quando Procurar um Advogado de Família?
              </h2>
              <p className="text-gray-600 leading-relaxed">
                Ao considerar o divórcio, quando precisa estabelecer ou revisar pensão alimentícia, 
                para definir a guarda dos filhos, regularizar uma união estável ou resolver 
                questões de partilha de bens.
              </p>
            </div>
          </div>
        </section>

        {/* Serviços */}
        <section className="py-20 bg-gray-50 relative overflow-hidden">
          <div className="absolute left-0 bottom-0 opacity-[0.02] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[400px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-10" style={{ fontFamily: "'Playfair Display', serif" }}>
              Nossos Serviços em Direito de Família
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {servicos.map((servico, index) => (
                <div key={index} className="bg-white p-6 rounded-lg border border-gray-100 hover:shadow-md transition-all">
                  <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center text-rose-700 mb-4">
                    {servico.icon}
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">{servico.title}</h4>
                  <p className="text-gray-600 text-sm">{servico.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Diferencial */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Nosso Compromisso com Você
                </h2>
                <p className="text-gray-600 mb-6">
                  Questões familiares exigem mais do que conhecimento técnico. Entendemos que você 
                  está passando por um momento delicado e oferecemos um atendimento humanizado, 
                  buscando sempre a melhor solução.
                </p>
                <div className="space-y-4">
                  {[
                    "Atendimento humanizado e sigiloso",
                    "Busca por soluções consensuais",
                    "Proteção dos interesses dos filhos",
                    "Agilidade nos processos extrajudiciais",
                    "Acompanhamento durante todo o processo",
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-rose-50 p-8 rounded-xl border border-rose-100">
                <Heart className="w-10 h-10 text-rose-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Divórcio Consensual</h3>
                <p className="text-gray-600 mb-6">
                  Quando há acordo entre as partes, o divórcio pode ser feito de forma rápida e 
                  menos custosa, seja em cartório ou de forma judicial simplificada.
                </p>
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-700 text-white px-6 py-3 rounded font-medium transition-all w-full">
                  <MessageCircle className="w-5 h-5" />
                  Tirar Dúvidas
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <HelpCircle className="w-10 h-10 text-rose-600 mx-auto mb-4" />
              <h2 
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Perguntas Frequentes
              </h2>
              <p className="text-gray-600">
                Tire suas principais dúvidas sobre Direito de Família
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-gray-900 pr-4">{faq.pergunta}</span>
                    <ChevronDown 
                      className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${
                        openFaq === index ? "rotate-180" : ""
                      }`} 
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-5 pt-0">
                      <p className="text-gray-600 leading-relaxed">{faq.resposta}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-rose-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Passando por uma Questão Familiar?
            </h2>
            <p className="text-rose-100 mb-8 max-w-xl mx-auto">
              Entre em contato para uma conversa confidencial. Estamos aqui para ajudar.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded font-medium transition-all">
                <MessageCircle className="w-5 h-5" />
                Falar pelo WhatsApp
              </a>
              <Link to="/contato"
                className="inline-flex items-center justify-center border-2 border-white/30 hover:border-white text-white px-8 py-4 rounded font-medium transition-all">
                Enviar Mensagem
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FamiliaPage;
